/* Vertex · PIXELLAB Soft LED: live LED sign preview and section interactions.
   Text is rasterised onto the real 16-row dot grid, so 35"/52"/70" previews show 96/144/192 columns. */
(() => {
  if (window.__vpLoaded) return;
  window.__vpLoaded = true;

  const ROWS = 16;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const COLORS = {
    amber: '#FFB020', red: '#FF3B30', green: '#39E05A', cyan: '#3FD0FF', white: '#F6F1E6', rainbow: null
  };

  function bitmap(text) {
    const c = document.createElement('canvas');
    const x = c.getContext('2d');
    const font = '800 15px ' + (getComputedStyle(document.body).getPropertyValue('--font-body--family') || 'Inter') + ', "Arial Black", Arial, sans-serif';
    x.font = font;
    const w = Math.max(1, Math.ceil(x.measureText(text).width) + 2);
    c.width = w; c.height = ROWS;
    x.font = font; x.fillStyle = '#fff'; x.textBaseline = 'alphabetic';
    x.fillText(text, 1, 13);
    const d = x.getImageData(0, 0, w, ROWS).data;
    const cols = [];
    for (let i = 0; i < w; i++) {
      const col = new Uint8Array(ROWS);
      for (let r = 0; r < ROWS; r++) col[r] = d[(r * w + i) * 4 + 3] > 110 ? 1 : 0;
      cols.push(col);
    }
    return cols;
  }

  class Led {
    constructor(canvas, { cols = 144, text = '', color = 'amber' } = {}) {
      this.canvas = canvas; this.ctx = canvas.getContext('2d');
      this.cols = cols; this.color = color; this.speed = 22;
      this.setText(text);
    }
    setText(t) { this.text = t || ' '; this.bm = bitmap(this.text); this.pos = this.cols; }
    setCols(n) { this.cols = n; this.pos = n; this.resize(); }
    resize() {
      const dpr = Math.min(2, devicePixelRatio || 1);
      const w = this.canvas.clientWidth || 300;
      const h = w * ROWS / this.cols;
      this.canvas.style.height = h + 'px';
      this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr);
    }
    draw(t) {
      const { ctx, canvas, cols, bm } = this;
      if (!canvas.width) this.resize();
      const W = canvas.width, H = canvas.height, p = W / cols, r = p * .36;
      ctx.fillStyle = '#0B0907'; ctx.fillRect(0, 0, W, H);
      const bw = bm.length, fits = bw <= cols;
      const total = bw + cols, off = Math.floor(this.pos) % total;
      const start = fits ? Math.floor((cols - bw) / 2) : 0;
      for (let c = 0; c < cols; c++) {
        const x = reduce ? c - start : c + off - cols;
        const lit = x >= 0 && x < bw ? bm[x] : null;
        for (let row = 0; row < ROWS; row++) {
          const cx = c * p + p / 2, cy = row * p + p / 2;
          if (lit && lit[row]) {
            const fill = this.color === 'rainbow' ? `hsl(${(c * 2.2 + t * .06) % 360} 100% 60%)` : COLORS[this.color];
            ctx.fillStyle = fill;
            ctx.globalAlpha = .22; ctx.beginPath(); ctx.arc(cx, cy, r * 1.9, 0, 6.283); ctx.fill();
            ctx.globalAlpha = 1; ctx.beginPath(); ctx.arc(cx, cy, r, 0, 6.283); ctx.fill();
          } else {
            ctx.globalAlpha = 1; ctx.fillStyle = '#2A2521'; ctx.beginPath(); ctx.arc(cx, cy, r * .8, 0, 6.283); ctx.fill();
          }
        }
      }
      ctx.globalAlpha = 1;
    }
  }

  const leds = new Set();
  let last = performance.now(), running = false;
  function loop(t) {
    const dt = Math.min(.1, (t - last) / 1000); last = t;
    for (const l of leds) {
      if (!l.canvas.isConnected) { leds.delete(l); continue; }
      if (l.canvas.offsetParent === null) continue;
      l.pos += l.speed * dt; l.draw(t);
    }
    requestAnimationFrame(loop);
  }
  function track(led) { leds.add(led); if (!running) { running = true; requestAnimationFrame(loop); } }
  addEventListener('resize', () => leds.forEach(l => l.resize()));

  const readJSON = (root, sel) => { try { return JSON.parse(root.querySelector(sel)?.textContent || 'null'); } catch { return null; } };
  const pct = (len, max) => (len / max * 100).toFixed(1) + '%';

  /* ---------- hero / any stage with controls ---------- */
  function initStage(root) {
    if (root.dataset.vpReady) return; root.dataset.vpReady = '1';
    const sizes = readJSON(root, '[data-vp-sizes]') || [];
    const packs = readJSON(root, '[data-vp-packs]') || [];
    const maxLen = Math.max(...sizes.map(s => s.len), 1);
    const input = root.querySelector('[data-vp-text]');
    const frame = root.querySelector('[data-vp-frame]');
    const startSize = sizes.find(s => s.key === root.dataset.vpStart) || sizes[1] || sizes[0];
    const led = new Led(root.querySelector('canvas'), { cols: startSize ? startSize.cols : 144, text: input ? input.value : 'OPEN', color: root.dataset.vpColor || 'amber' });
    track(led);
    const count = () => { const c = root.querySelector('[data-vp-count]'); if (c && input) c.textContent = `${input.value.length}/60`; };
    const setText = t => { if (input) input.value = t; led.setText(t); count(); };
    input?.addEventListener('input', () => { led.setText(input.value); count(); });

    function setSize(key) {
      const s = sizes.find(x => x.key === key); if (!s) return;
      root.querySelectorAll('[data-vp-size]').forEach(b => b.setAttribute('aria-pressed', b.dataset.vpSize === key));
      if (frame) frame.style.width = pct(s.len, maxLen);
      const meta = root.querySelector('[data-vp-meta]'); if (meta) meta.textContent = `${s.inch}″ · 16 × ${s.cols} dots`;
      const leds = root.querySelector('[data-vp-leds]'); if (leds) leds.textContent = `${s.leds.toLocaleString('en-US')} LEDs`;
      root.closest('.vp-hero')?.querySelectorAll('[data-vp-price-for]').forEach(o => { o.hidden = o.dataset.vpPriceFor !== key; });
      led.setCols(s.cols); setTimeout(() => led.resize(), 470);
    }
    root.querySelectorAll('[data-vp-size]').forEach(b => b.addEventListener('click', () => setSize(b.dataset.vpSize)));
    root.querySelectorAll('[data-vp-color]').forEach(b => b.setAttribute('aria-pressed', b.dataset.vpColor === led.color));
    root.querySelectorAll('[data-vp-color]').forEach(b => b.addEventListener('click', () => {
      led.color = b.dataset.vpColor;
      root.querySelectorAll('[data-vp-color]').forEach(x => x.setAttribute('aria-pressed', x === b));
    }));

    const pc = root.querySelector('[data-vp-presets]'), ic = root.querySelector('[data-vp-industry]');
    function showPack(i) {
      const p = packs[i]; if (!p || !pc) return;
      ic?.querySelectorAll('button').forEach((b, j) => b.setAttribute('aria-pressed', j === i));
      pc.innerHTML = '';
      p.messages.forEach(m => {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'vp-preset'; b.textContent = m;
        b.addEventListener('click', () => setText(m)); pc.appendChild(b);
      });
      setText(p.messages[0]);
    }
    if (ic && packs.length) {
      packs.forEach((p, i) => {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'vp-chip'; b.textContent = p.name;
        b.setAttribute('aria-pressed', 'false'); b.addEventListener('click', () => showPack(i)); ic.appendChild(b);
      });
      showPack(0);
    }
    if (startSize) setSize(startSize.key);
    count();
    root.__vpLed = led; root.__vpSetSize = setSize; root.__vpSizes = sizes;
  }

  /* ---------- product page: follow the selected variant ---------- */
  function initPdpPreview(root) {
    initStage(root);
    const sizes = root.__vpSizes || [];
    const match = title => {
      const n = parseInt(String(title || '').replace(/[^0-9]/g, ' ').trim().split(/\s+/)[0], 10);
      return sizes.find(s => s.inch === n);
    };
    const fromVariant = v => { const s = match(v?.option1 || v?.title || v?.name); if (s) root.__vpSetSize(s.key); };
    const first = readJSON(root, '[data-vp-current-variant]'); if (first) fromVariant(first);
    document.addEventListener('variant:update', e => fromVariant(e.detail?.resource || e.detail?.variant || e.detail?.data?.variant));
    document.addEventListener('change', e => {
      const el = e.target; if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
      if (!el.closest('variant-picker, [data-variant-picker], .variant-picker')) return;
      const s = match(el.value); if (s) root.__vpSetSize(s.key);
    });
  }

  /* ---------- window-width fit tool ---------- */
  function initFit(root) {
    if (root.dataset.vpReady) return; root.dataset.vpReady = '1';
    const sizes = (readJSON(root, '[data-vp-sizes]') || []).slice().sort((a, b) => b.len - a.len);
    const input = root.querySelector('input'), out = root.querySelector('output');
    const run = () => {
      const w = parseFloat(input.value);
      if (!w || w < 20) { out.textContent = 'Enter the width of the glass in inches.'; return; }
      const pick = sizes.find(s => s.len <= w - 6);
      if (!pick) { out.innerHTML = `The smallest sign needs about ${Math.ceil(sizes[sizes.length - 1].len + 6)}″ of glass. For a narrower spot, hang it inside above the counter.`; return; }
      const two = sizes.find(s => s.len * 2 + 4 <= w - 6);
      out.innerHTML = `The <b>${pick.inch}″ sign</b> fits with room to spare.` + (two && pick === sizes[0] ? ` You could also run <b>two ${two.inch}″ signs</b> side by side.` : '');
    };
    input.addEventListener('input', run); run();
  }

  /* ---------- video wall + click-to-play videos ---------- */
  function initVideos(root) {
    if (root.dataset.vpVidReady) return; root.dataset.vpVidReady = '1';
    const hover = matchMedia('(hover:hover) and (pointer:fine)').matches && !reduce;
    root.querySelectorAll('[data-vp-vid]').forEach(f => {
      const v = f.querySelector('video'), b = f.querySelector('[data-vp-play]'); if (!v) return;
      const play = () => v.play().then(() => { if (b) b.textContent = '❚❚'; }).catch(() => {});
      const pause = () => { v.pause(); if (b) b.textContent = '▶'; };
      const toggle = () => (v.paused ? play() : pause());
      b?.addEventListener('click', toggle); v.addEventListener('click', toggle);
      if (hover) { f.addEventListener('mouseenter', play); f.addEventListener('mouseleave', pause); }
    });
    root.querySelectorAll('[data-vp-click]').forEach(w => {
      const v = w.querySelector('video'), b = w.querySelector('button');
      b?.addEventListener('click', () => {
        w.classList.add('is-playing'); v.controls = true; v.muted = false;
        v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
        v.focus?.();
      });
    });
    const filter = root.querySelector('[data-vp-filter]');
    filter?.querySelectorAll('button').forEach(b => b.addEventListener('click', () => {
      const c = b.dataset.cat;
      filter.querySelectorAll('button').forEach(x => x.setAttribute('aria-pressed', x === b));
      root.querySelectorAll('[data-vp-vid][data-cat]').forEach(f => {
        f.hidden = c !== 'all' && f.dataset.cat !== c;
        if (f.hidden) f.querySelector('video')?.pause();
      });
    }));
    if (reduce) root.querySelectorAll('video[autoplay]').forEach(v => { v.pause(); v.removeAttribute('autoplay'); });
  }


  /* ---------- phone buy bar: appears once the theme's Add to cart button is out of view ---------- */
  function initSticky(bar) {
    if (bar.dataset.vpReady) return; bar.dataset.vpReady = '1';
    const main = document.querySelector('form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] [name="add"]');
    if (!main) return;
    bar.querySelector('[data-vp-sticky-add]')?.addEventListener('click', () => main.click());
    new IntersectionObserver(([e]) => { bar.hidden = e.isIntersecting || e.boundingClientRect.top > 0; }).observe(main);
  }

  /* ---------- fold long video walls behind a "Show more" button ---------- */
  function initFold(root) {
    const grid = root.querySelector('.vp-videos'); if (!grid || grid.dataset.vpFold) return;
    const limit = matchMedia('(max-width: 749px)').matches ? Number(grid.dataset.foldMobile || 6) : Number(grid.dataset.foldDesktop || 8);
    const clips = [...grid.querySelectorAll('.vp-vid')];
    if (clips.length <= limit) return;
    grid.dataset.vpFold = '1';
    clips.forEach((c, i) => c.classList.toggle('is-extra', i >= limit));
    grid.classList.add('is-folded');
    const wrap = document.createElement('div'); wrap.className = 'vp-more';
    const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'vp-btn vp-btn--ghost';
    btn.textContent = `Show all ${clips.length} stores`;
    btn.addEventListener('click', () => { grid.classList.remove('is-folded'); wrap.remove(); });
    wrap.appendChild(btn); grid.after(wrap);
    // picking a business type shows every matching clip
    root.querySelector('[data-vp-filter]')?.addEventListener('click', () => { grid.classList.remove('is-folded'); wrap.remove(); }, { once: true });
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-vp-stage]').forEach(initStage);
    scope.querySelectorAll('[data-vp-pdp]').forEach(initPdpPreview);
    scope.querySelectorAll('[data-vp-fit]').forEach(initFit);
    // Video posters load only when the video is near the screen, so a long video wall does not cost data up front.
    const posters = scope.querySelectorAll('video[data-poster]');
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.poster = e.target.dataset.poster; e.target.removeAttribute('data-poster'); io.unobserve(e.target); } }), { rootMargin: '600px 0px' });
      posters.forEach(v => io.observe(v));
    } else posters.forEach(v => { v.poster = v.dataset.poster; });
    scope.querySelectorAll('[data-vp-videos]').forEach(initVideos);
    scope.querySelectorAll('.vp-light-wall, .vp-dark[data-vp-videos]').forEach(initFold);
    scope.querySelectorAll('[data-vp-sticky]').forEach(initSticky);
    scope.querySelectorAll('[data-vp-more-btn]').forEach(b => {
      if (b.dataset.vpReady) return; b.dataset.vpReady = '1';
      b.addEventListener('click', () => {
        const open = b.getAttribute('aria-expanded') !== 'true';
        b.setAttribute('aria-expanded', open);
        document.getElementById(b.getAttribute('aria-controls'))?.classList.toggle('is-open', open);
      });
    });
  }
  const boot = () => initAll();
  (document.fonts?.ready || Promise.resolve()).then(boot);
  // Theme editor: re-initialise a section after the merchant edits it.
  document.addEventListener('shopify:section:load', e => initAll(e.target));
})();
